import { useRef, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload, Trash2, FileArchive, Sparkles, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import type { AdTemplate } from "@shared/schema";

/**
 * Admin → Global Ad Templates. Anything uploaded here gets stamped
 * `is_global: true` and surfaces to every user inside the Canvas Ad Library
 * "Ad templates" tab automatically (no per-user opt-in).
 */
export default function AdminAdTemplates() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);
  const [autoAnalyze, setAutoAnalyze] = useState(true);
  const [search, setSearch] = useState("");

  const { data: templates = [], isLoading } = useQuery<AdTemplate[]>({
    queryKey: ["/api/admin/ad-templates"],
    queryFn: async () => {
      const res = await fetch("/api/admin/ad-templates");
      if (!res.ok) throw new Error("Failed to load global templates");
      return res.json();
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      if (autoAnalyze) fd.append("analyze", "1");
      const res = await fetch("/api/admin/ad-templates", { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json()).message || "Upload failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ad-templates"] });
      // Invalidate user-facing list too — admins viewing the canvas Ad Library
      // should see the new global immediately.
      queryClient.invalidateQueries({ queryKey: ["/api/ad-templates"] });
      toast({ title: "Global template uploaded" });
    },
    onError: (err: any) => {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    },
  });

  const batchMutation = useMutation({
    mutationFn: async (zipFile: File) => {
      const fd = new FormData();
      fd.append("file", zipFile);
      if (autoAnalyze) fd.append("analyze", "1");
      const res = await fetch("/api/admin/ad-templates/batch", { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json()).message || "Batch upload failed");
      return res.json() as Promise<{ success: number; failed: number; templates: AdTemplate[] }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ad-templates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ad-templates"] });
      toast({
        title: `Batch upload complete`,
        description: `${data.success} added, ${data.failed} failed`,
      });
    },
    onError: (err: any) => {
      toast({ title: "Batch upload failed", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/ad-templates/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ad-templates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ad-templates"] });
      toast({ title: "Template deleted" });
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) =>
      [t.name, t.description, ...(t.niche || []), ...(t.styleTags || [])]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [templates, search]);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Global Ad Templates</h1>
          <p className="text-sm text-gray-400 mt-1">
            Anything uploaded here is visible to every user in their canvas Ad Library. Users can
            still upload their own private templates from inside the app.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <Sparkles className="h-4 w-4 text-amber-400" />
            <span>AI auto-categorize</span>
            <Switch checked={autoAnalyze} onCheckedChange={setAutoAnalyze} />
          </label>
          <Button
            variant="outline"
            onClick={() => zipInputRef.current?.click()}
            disabled={batchMutation.isPending}
            className="border-gray-700 text-gray-200 hover:bg-gray-800"
          >
            {batchMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Uploading ZIP…</>
            ) : (
              <><FileArchive className="h-4 w-4 mr-1.5" /> Upload ZIP</>
            )}
          </Button>
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {uploadMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Uploading…</>
            ) : (
              <><Upload className="h-4 w-4 mr-1.5" /> Upload Template</>
            )}
          </Button>
        </div>
      </header>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const files = e.target.files;
          if (!files) return;
          Array.from(files).forEach((f) => uploadMutation.mutate(f));
          e.target.value = "";
        }}
        multiple
      />
      <input
        ref={zipInputRef}
        type="file"
        accept=".zip"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) batchMutation.mutate(file);
          e.target.value = "";
        }}
      />

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, niche, or style…"
          className="pl-9 bg-gray-900 border-gray-700 text-gray-100 placeholder:text-gray-500"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-gray-700 rounded-lg p-12 text-center text-gray-500">
          {templates.length === 0
            ? "No global templates yet — upload some to seed every user's library."
            : "No templates match your search."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filtered.map((t) => (
            <div
              key={t.id}
              className="group relative rounded-lg overflow-hidden border border-gray-800 bg-gray-900"
            >
              <div className="aspect-square bg-black/40 overflow-hidden">
                <img
                  src={t.imageUrl}
                  alt={t.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <button
                onClick={() => {
                  if (window.confirm(`Delete global template "${t.name}"? This will remove it from every user's library.`)) {
                    deleteMutation.mutate(t.id);
                  }
                }}
                className="absolute top-2 right-2 rounded-full bg-black/70 hover:bg-red-600/90 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete"
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              <div className="p-3">
                <div className="text-sm font-medium text-gray-100 truncate" title={t.name}>{t.name}</div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {(t.niche || []).slice(0, 3).map((n) => (
                    <span key={n} className="text-[10px] px-1.5 py-0.5 bg-indigo-900/60 text-indigo-200 rounded">
                      {n}
                    </span>
                  ))}
                  {(t.styleTags || []).slice(0, 2).map((s) => (
                    <span key={s} className="text-[10px] px-1.5 py-0.5 bg-gray-800 text-gray-300 rounded">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
